export default function createLastSevenDaysLabels() {
  const labels = [];
  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(today.getDate() - i);

    // Format the date as "YYYY-MM-DD"
    const label = date.toISOString().split("T")[0];
    labels.push(label);
  }

  return labels.reverse(); // Reverse to get the order from oldest to newest
}

export const changeDateFormat = (data) => {
  const dateStr = data;
  const date = new Date(dateStr);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

const createGradient = (ctx, color1, color2) => {
  if (ctx) {
    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  }
};

export function fetchColorByStatus(status, key, ctx) {
  let status_color = [
    {
      status: "Order Accepted",
      background_color: "#007bff",
      borderColor: "#0056b3",
    },
    { status: "Accepted", background_color: "#6f42c1", borderColor: "#5a31a1" },
    {
      status: "Fulfilled",
      background_color: "#28a745",
      borderColor: "#1e7e34",
    },
    { status: "Shipped", background_color: "#17a2b8", borderColor: "#117a8b" },
    {
      status: "In Transit",
      background_color: "#0070FF",
      borderColor: "#0070FF",
      gradient: createGradient(ctx, "#0070FF90", "#0070FF"),
    },
    {
      status: "Custom Clearance",
      background_color: "#3399FF",
      borderColor: "#3399FF",
      gradient: createGradient(ctx, "#3399FF90", "#3399FF"),
    },
    {
      status: "Out for Delivery",
      background_color: "#00A32D",
      borderColor: "#00A32D",
      gradient: createGradient(ctx, "#00A32D90", "#00A32D"),
    },
    {
      status: "Delivered",
      background_color: "#008B0E",
      borderColor: "#008B0E",
      gradient: createGradient(ctx, "#008B0E90", "#008B0E"),
    },
    {
      status: "On Hold",
      background_color: "#dc354590",
      borderColor: "#c8233390",
    },
    {
      status: "Cancelled",
      background_color: "#6c757d",
      borderColor: "#5a6268",
    },
    {
      status: "Undelivered",
      background_color: "#FF1100",
      borderColor: "#FF1100",
    },
  ];
  return status_color.find((x) => x.status === status)[key];
}

export const generateColor = () => {
  let color = "#";
  let digits = "0123456789ABCDEF";
  for (let i = 0; i < 6; i++) {
    let randomDigit = Math.floor(Math.random() * 16);
    color += digits[randomDigit];
  }

  return color;
};

export const shadeColor = (color, percent) => {
  var R = parseInt(color.substring(1, 3), 16);
  var G = parseInt(color.substring(3, 5), 16);
  var B = parseInt(color.substring(5, 7), 16);

  R = parseInt((R * (100 + percent)) / 100);
  G = parseInt((G * (100 + percent)) / 100);
  B = parseInt((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  R = Math.round(R);
  G = Math.round(G);
  B = Math.round(B);

  var RR = R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16);
  var GG = G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16);
  var BB = B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16);

  return "#" + RR + GG + BB;
};

export const formatKey = (key) => {
  const parts = key.split(".");

  const formattedParts = parts.map((part) => {
    return part
      .split("_")
      .map((word) => {
        // Custom handling for specific words
        if (word.toUpperCase() === "ID") return "ID";
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  });

  return formattedParts.join(" > ");
};

export function buildChartData(rows, opts = {}) {
  const {
    currentLabel = "Current Period",
    compareLabel = "Previous Period",
    compareMode,
  } = opts;

  /* ---------- detect granularity ---------- */
  const isHourly =
    rows.length && Object.prototype.hasOwnProperty.call(rows[0], "hour");

  /* ---------- build x‑axis labels ---------- */
  const range = isHourly
    ? Array.from({ length: 24 }, (_, i) => i) // 0..23
    : Array.from(
        { length: ["WNY", "WOW"].includes(compareMode) ? 7 : 31 },
        (_, i) => i + 1
      );

  const labelFmt = (idx) =>
    isHourly ? String(idx).padStart(2, "0") + ":00" : idx.toString();

  const labels = range.map(labelFmt);
  const currentArr = range.map(() => null);
  const compareArr = range.map(() => null);

  /* ---------- fill series ---------- */
  rows.forEach((r) => {
    const slot = isHourly ? r.hour : r.day;
    if (slot == null) return;
    const idx = isHourly ? slot : slot - 1; // day 1 → index 0
    if (idx < 0 || idx >= range.length) return;

    if (r.isCurrentPeriod) currentArr[idx] = r.fillRate;
    else compareArr[idx] = r.fillRate;
  });

  /* ---------- return chart‑js structure ---------- */
  return {
    labels,
    datasets: [
      {
        label: compareLabel,
        data: compareArr,
        borderColor: "#f9a8d4",
        backgroundColor: "#f9a8d4",
        tension: 0.3,
        pointRadius: 4,
      },
      {
        label: currentLabel,
        data: currentArr,
        borderColor: "#60a5fa",
        backgroundColor: "#60a5fa",
        tension: 0.3,
        pointRadius: 4,
      },
    ],
  };
}

export function randomUid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const padNumberWithZeros = (number, minLength = 6) => {
  return number.toString().padStart(minLength, "0");
};

export const checkIfFiltered = (filterData, key) => {
  if (
    filterData?.columns?.find((item) => item.key === key)?.type === "date-range"
  ) {
    return !(
      filterData?.columns?.find((item) => item.key === key)?.filter
        ?.startDate &&
      filterData?.columns?.find((item) => item.key === key)?.filter?.endDate
    );
  } else if (
    filterData?.columns?.find((item) => item.key === key)?.type ===
    "number-range"
  ) {
    return !(
      filterData?.columns?.find((item) => item.key === key)?.filter?.min &&
      filterData?.columns?.find((item) => item.key === key)?.filter?.max
    );
  } else {
    return !filterData?.columns?.find((item) => item.key === key)?.filter
      ?.length;
  }
};

export const roundToThree = (num) => {
  const factor = 10 ** 3; // 1000
  return Math.round((Number(num) + Number.EPSILON) * factor) / factor;
};
